import { render } from '@markbird/markdown';
import { db } from './db';
import { resolveTarget } from './linkIndex';
import { resolveSection } from './sections';
import { parseWikiRefs, type WikiRef } from './wikilinks';

/**
 * Transclusion: `![[Note]]`, `![[Note#Heading]]`, `![[Note^block]]`.
 *
 * Like wikilinks, embeds are resolved against the local library in the preview
 * DOM rather than in the shared render pipeline — an export or a share link has
 * no library to resolve against, and silently inlining someone else's notes
 * into a shared document would leak them. There the `![[…]]` text stands as
 * written.
 *
 * The embedded markdown goes back through the same `render()` as everything
 * else, so it is sanitized on the same terms and cannot smuggle in scripts.
 */

/** How deep an embed may nest before we stop following it. */
const MAX_DEPTH = 3;

/** Marks the placeholder the preview pass leaves behind for each embed. */
const EMBED_CLASS = 'mb-embed';

export interface EmbedLabels {
  /** Shown when the target document does not exist. */
  missing: (target: string) => string;
  /** Shown when the document exists but the heading or block does not. */
  missingSection: (section: string) => string;
  /** Shown where an embed chain is cut off, by a cycle or by sheer depth. */
  truncated: (target: string) => string;
}

const DEFAULT_LABELS: EmbedLabels = {
  missing: (target) => `Cannot embed “${target}” — no such document.`,
  missingSection: (section) => `Cannot embed “${section}” — not found in that document.`,
  truncated: (target) => `“${target}” is embedded further up; not repeating it here.`,
};

/**
 * Replace embed references in text nodes with placeholder blocks.
 *
 * Deliberately synchronous and separate from filling them: the caller runs its
 * wikilink pass straight afterwards, and `![[X]]` has to have become an element
 * by then — otherwise that pass would turn the `[[X]]` inside it into a link
 * and leave a stray `!` behind.
 */
export function placeEmbeds(root: HTMLElement): HTMLElement[] {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const p = node.parentElement;
      if (!p || p.closest('a, code, pre')) return NodeFilter.FILTER_REJECT;
      return (node.nodeValue ?? '').includes('![[')
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  });

  const texts: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) texts.push(n as Text);

  const placeholders: HTMLElement[] = [];
  for (const text of texts) {
    const value = text.nodeValue ?? '';
    const refs = parseWikiRefs(value).filter((r) => r.embed);
    if (refs.length === 0) continue;

    const frag = document.createDocumentFragment();
    let last = 0;
    for (const ref of refs) {
      if (ref.start > last) frag.appendChild(document.createTextNode(value.slice(last, ref.start)));
      const el = document.createElement('div');
      el.className = EMBED_CLASS;
      el.dataset.title = ref.rawTarget;
      if (ref.heading) el.dataset.heading = ref.heading;
      if (ref.block) el.dataset.block = ref.block;
      frag.appendChild(el);
      placeholders.push(el);
      last = ref.end;
    }
    if (last < value.length) frag.appendChild(document.createTextNode(value.slice(last)));
    text.parentNode?.replaceChild(frag, text);
  }
  return placeholders;
}

/** Render a "this embed does not resolve" note in place of content. */
function fillMissing(el: HTMLElement, message: string): void {
  el.classList.add('mb-embed--missing');
  el.textContent = message;
}

function refOf(el: HTMLElement): Pick<WikiRef, 'rawTarget' | 'heading' | 'block'> {
  return {
    rawTarget: el.dataset.title ?? '',
    heading: el.dataset.heading ?? null,
    block: el.dataset.block ?? null,
  };
}

export interface EmbedOptions {
  labels?: EmbedLabels;
  depth?: number;
  seen?: Set<string>;
  /**
   * Called with each embed element once its markdown is in the DOM, so the
   * caller can run the rest of its preview pipeline — math, diagrams, images,
   * wikilinks — over embedded content on the same terms as the host document.
   */
  onRendered?: (el: HTMLElement) => void;
}

/**
 * Fill placed embeds with their target content.
 *
 * `seen` carries the documents already on the current embed path, so a note
 * that embeds itself — directly or through a cycle — stops instead of hanging
 * the tab, and `depth` stops a merely deep chain from doing the same.
 */
export async function fillEmbeds(
  placeholders: HTMLElement[],
  options: EmbedOptions = {},
): Promise<void> {
  const labels = options.labels ?? DEFAULT_LABELS;
  const depth = options.depth ?? 0;
  const seen = options.seen ?? new Set<string>();
  if (placeholders.length === 0) return;

  await Promise.all(
    placeholders.map(async (el) => {
      const ref = refOf(el);
      const id = await resolveTarget(ref.rawTarget);
      if (!id) {
        fillMissing(el, labels.missing(ref.rawTarget));
        return;
      }
      if (seen.has(id) || depth >= MAX_DEPTH) {
        el.classList.add('mb-embed--truncated');
        el.dataset.docId = id;
        el.textContent = labels.truncated(ref.rawTarget);
        return;
      }

      const doc = await db.documents.get(id);
      if (!doc) {
        fillMissing(el, labels.missing(ref.rawTarget));
        return;
      }
      const markdown = resolveSection(doc.content, ref);
      if (markdown === null) {
        fillMissing(el, labels.missingSection(ref.heading ?? ref.block ?? ''));
        return;
      }

      el.dataset.docId = id;
      el.innerHTML = render(markdown);
      // Nested embeds resolve on the same terms, one level further down.
      const nested = placeEmbeds(el);
      options.onRendered?.(el);
      await fillEmbeds(nested, {
        ...options,
        depth: depth + 1,
        seen: new Set([...seen, id]),
      });
    }),
  );
}

/** Place and fill every embed under `root`. */
export async function resolveEmbedsInDom(
  root: HTMLElement,
  options: EmbedOptions = {},
): Promise<void> {
  await fillEmbeds(placeEmbeds(root), options);
}
